import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { createPortal } from 'react-dom';
import toast, { Toaster } from 'react-hot-toast';

import useAppSelector from '@/hooks/useAppSelector';
import { AlertTriangle, Info } from 'lucide-react';
import { useTheme } from 'styled-components';

type ExtendedToast = typeof toast & {
  warning: (message: string) => void;
  info: (message: string) => void;
};

interface IToast {
  toast: ExtendedToast;
}

const ToastContext = createContext<IToast>({} as IToast);

export const ToastProvider = ({ children }: { children: React.ReactNode }) => {
  const chatInternal = useAppSelector((state) => state.chatInterno.chatInterno);
  const chatWindow = useAppSelector((state) => state.chatWindow.chatWindow);

  const { colors, zIndex } = useTheme();

  const extendedToast = useMemo(() => {
    const extended = toast as ExtendedToast;
    extended.warning = (message: string) => {
      toast(message, {
        icon: React.createElement(AlertTriangle, {
          size: 20,
          color: colors.white,
        }),
        style: {
          background: colors.warning,
          color: colors.white,
        },
      });
    };
    extended.info = (message: string) => {
      toast(message, {
        icon: React.createElement(Info, {
          size: 20,
          color: colors.white,
        }),
        style: {
          background: colors.info,
          color: colors.white,
        },
      });
    };
    return extended;
  }, [colors]);

  const [isMounted, setIsMounted] = useState(false);

  useEffect(() => {
    setIsMounted(true);
  }, []);

  const toastPosition = useMemo(() => {
    if (chatWindow?.id && chatInternal && !chatInternal.isChatModal) {
      return {
        bottom: 72,
        right: 32,
      };
    }

    return {
      bottom: 8,
      right: 32,
    };
  }, [chatWindow, chatInternal]);

  return (
    <ToastContext.Provider value={{ toast: extendedToast }}>
      {children}

      {isMounted &&
        createPortal(
          <Toaster
            position="bottom-right"
            reverseOrder={false}
            containerStyle={{
              bottom: toastPosition.bottom,
              right: toastPosition.right,
              zIndex: zIndex.notification,
            }}
            toastOptions={{
              style: {
                display: 'flex',
                background: colors.warning,
                color: colors.white,
                maxWidth: '750px',
              },
              duration: 3000,
              loading: {
                style: {
                  display: 'flex',
                  justifyContent: 'center',
                  background: colors.white,
                  color: colors.black,
                },
                duration: 3000,
              },
              custom: {
                style: {
                  display: 'flex',
                  justifyContent: 'center',
                  background: colors.success,
                  color: colors.white,
                },
                duration: 3000,
              },
              error: {
                style: {
                  display: 'flex',
                  justifyContent: 'center',
                  background: colors.danger,
                  color: colors.white,
                  maxWidth: '900px',
                },
                duration: 3000,
              },
              success: {
                style: {
                  display: 'flex',
                  justifyContent: 'center',
                  background: colors.success,
                  color: colors.white,
                },
                duration: 3000,
              },
            }}
          />,
          document.body,
        )}
    </ToastContext.Provider>
  );
};

export function useToast() {
  const context = useContext(ToastContext);

  return context;
}
